import { Injectable } from '@angular/core';
import { AddressDTO, CompanyDTO, LocationDTO } from '../api/api';
import { TranslateService } from '@ngx-translate/core';
import { SearchOptions } from '@ngx-seatback/controls';

@Injectable({ providedIn: 'root' })
export class StringTransformationService {
  public wildcard: string = '%';

  constructor(private readonly translate: TranslateService) {}

  public addLeadingZeros(num: number, totalLength: number): string {
    return String(num).padStart(totalLength, '0');
  }

  toCamelCase(str: string): string {
    let retVal: string;

    retVal = str
      .trim()
      .replace(/[^A-Za-z]/g, ' ') /* clean up non-letter characters */
      .replace(/(.)/g, (a, l) => l.toLowerCase())
      .replace(/(\s.)/g, (a, l) => l.toUpperCase())
      .replace(/[^A-Za-z\u00C0-\u00ff]/g, '');

    return retVal;
  }

  toSnakeCase(str: string): string {
    let retVal: string;

    retVal = str
      .trim()
      .replace(/([a-z])([A-Z])/g, '$1_$2') // Insert underscore between camelCase transitions
      .replace(/[^a-zA-Z0-9_]/g, '_') // Replace non-alphanumeric characters with underscores
      .toLowerCase(); // Convert everything to lowercase

    return retVal;
  }

  stringNotNull(s: string) {
    return !!s || s === '';
  }

  capitalizeString(inputString: string) {
    if (!inputString || typeof inputString !== 'string') {
      return null;
    }
    return inputString.charAt(0).toUpperCase() + inputString.slice(1);
  }

  locationOrCompanyToShortString(location: LocationDTO | CompanyDTO) {
    if (!location) {
      return null;
    }
    let result = '';
    const hasName: boolean = location.name && location.name.length > 0;
    const hasIdentifier: boolean = location.identifier && location.identifier.length > 0;

    if (hasName && hasIdentifier) {
      result = location.name + ' (' + location.identifier + ')';
    }

    if (!hasName && hasIdentifier) {
      result = location.identifier;
    }

    if (hasName && !hasIdentifier) {
      result = location.name;
    }

    return result;
  }

  locationOrCompanyToShortWithId(location: LocationDTO | CompanyDTO) {
    if (!location) {
      return null;
    }
    let result = '';
    const hasName: boolean = location.name && location.name.length > 0;
    const hasIdentifier: boolean = location.identifier && location.identifier.length > 0;

    if (hasName) {
      result = location.name + ' (' + location.id + ')';
    }

    if (!hasName && hasIdentifier) {
      result = location.identifier + ' (' + location.id + ')';
    }

    return result;
  }

  addressToFullString(address: AddressDTO): string {
    let result = '';
    if (address) {
      result += this.stringOrEmpty(address.street);
      result += this.stringOrEmpty(address.street2);
      result += this.stringOrEmpty(address.zip);
      result += this.stringOrEmpty(address.city);
      result += this.stringOrEmpty(address.borough);
      result += this.stringOrEmpty(address.state);
      result += this.stringOrEmpty(address.county);
      result += this.stringOrEmpty(address.country);

      result = result.trim();
    } else {
      return null;
    }

    return result;
  }

  addressToString(address: AddressDTO): string {
    let ret = '';

    ret +=
      (address.street ? address.street + ', ' : '') +
      (address.street2 ? address.street2 + ', ' : '') +
      (address.zip ? address.zip + ', ' : '') +
      (address.city ? address.city + ', ' : '') +
      (address.borough ? address.borough + ', ' : '') +
      (address.county ? address.county + ', ' : '') +
      (address.state ? address.state + ', ' : '') +
      (address.country ? address.country + ', ' : '');

    if (ret.length > 0) {
      return ret.substring(0, ret.length - 2);
    }

    return ret;
  }

  stringOrEmpty(str: string): string {
    if (str && str.length > 0) {
      return str + ' ';
    }
    return '';
  }

  truncateNumber(inputNumber: number, kIncluded?: boolean) {
    if (inputNumber === undefined || inputNumber === null) {
      return '';
    }
    const stgNumber = inputNumber;
    const numberDigits = inputNumber.toString().split('.')[0].length;
    switch (this.translate.getDefaultLang()) {
      case 'de': {
        switch (true) {
          case numberDigits > 9: {
            return this.shortString(stgNumber, 9) + ' Mrd.';
          }
          case numberDigits > 6: {
            return this.shortString(stgNumber, 6) + ' Mio.';
          }
          case numberDigits > 3: {
            return this.shortString(stgNumber, 3) + ' k';
          }
          case numberDigits <= 3: {
            return (Math.round(stgNumber * 1000) / 1000).toString();
          }
        }
        break;
      }
      case 'en': {
        switch (true) {
          case numberDigits > 9: {
            return this.shortString(stgNumber, 9) + ' Bil.';
          }
          case numberDigits > 6: {
            return this.shortString(stgNumber, 6) + ' Mio.';
          }
          case numberDigits > 3: {
            return this.shortString(stgNumber, 3) + ' k';
          }
          case numberDigits <= 3: {
            return (Math.round(stgNumber * 1000) / 1000).toString();
          }
        }
        break;
      }
    }
  }

  shortString(num: number, digits: number) {
    const factor = Math.pow(10, digits);
    const value = Math.round(num / factor);
    return value.toString().split('.')[0];
  }

  getPaginationString(page: number, maxSize: number, currentCount: number, resultCount: number, itemName: string = 'items') {
    const start = (page - 1) * maxSize + 1;
    const end = currentCount + start - 1;
    const total = resultCount;

    if (total <= 0) {
      return this.translate.instant('lang.globals.noData');
    }

    const itemNameSingular = this.translate.instant('pagination.singular.' + itemName);
    const itemNamePlural = this.translate.instant('pagination.plural.' + itemName);

    if (total && !isNaN(start) && !isNaN(end)) {
      return this.translate.instant('pagination.result', {
        start: this.transformNumber(start),
        end: this.transformNumber(end),
        total: this.transformNumber(total),
        item: total > 1 ? itemNamePlural : itemNameSingular,
      });
    }
    if (itemName === 'items') {
      return this.translate.instant('lang.globals.noElementsFound');
    }
    return this.translate.instant('lang.globals.noItemFound', { item: itemNamePlural });
  }

  transformNumber(num: number): string {
    const sep = '.';
    const comma = ',';
    let numStr: string;
    let decStr = '';
    if (('' + num).includes('.')) {
      numStr = ('' + num).split('.')[0];
      decStr = ('' + num).split('.')[1];
    } else {
      numStr = '' + num;
    }
    if (numStr.length > 3) {
      const modulo = numStr.length % 3;
      let output = '';
      if (modulo > 0) {
        output = numStr.substring(0, modulo);
      }
      const max = Math.floor(numStr.length / 3);
      for (let i = 0; i < max; i++) {
        if (modulo === 0 && i === 0) {
          output += numStr.substring(modulo + 3 * i, modulo + 3 * i + 3);
        } else {
          output += sep + numStr.substring(modulo + 3 * i, modulo + 3 * i + 3);
        }
      }
      if (decStr.length <= 0) {
        return output;
      } else {
        return output + comma + decStr;
      }
    } else if (decStr.length <= 0) {
      return numStr;
    } else {
      return numStr + comma + decStr;
    }
  }

  escapeHtml(str: string) {
    // truncate does not care about Types so a type check is needed in order for replace not to error
    if (typeof str !== 'string') {
      str = String(str);
    }
    if (!str) {
      return str;
    } else {
      return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/'/g, '&quot;').replace(/'/g, '&#039;');
    }
  }

  public encodeSearchQueryParam(exactMatch: boolean, search: string, wildcard: string = this.wildcard): string {
    // following statement can be removed in api calls with search function
    // this.options.search ? (this.options.search.length === 0 ? null :
    // this.util.encodeSearchQueryParam(this.options.exactMatch, this.options.search)) : null

    if (!search) {
      return null;
    }
    if (search.length === 0) {
      return null;
    }

    if (exactMatch) {
      return search;
    } else {
      return encodeURIComponent(wildcard + search + wildcard);
    }
  }

  public encodeSearchQueryParamWithOptions(exactMatch: SearchOptions, search: string, wildcard: string = this.wildcard): string {
    // following statement can be removed in api calls with search function
    // this.options.search ? (this.options.search.length === 0 ? null :
    // this.util.encodeSearchQueryParam(this.options.exactMatch, this.options.search)) : null

    if (!search) {
      return null;
    }
    if (search.length === 0) {
      return null;
    }

    switch (exactMatch) {
      case 'exact':
        return search;
      case 'suffix':
        return encodeURIComponent(wildcard + search);
      case 'prefix':
        return encodeURIComponent(search + wildcard);
      case 'contains':
      default:
        return encodeURIComponent(wildcard + search + wildcard);
    }
  }
}
